import { FC, useState } from 'react'
import {
    Tbody,
    Table,
    Th,
    Thead,
    Heading,
    Box,
    Tooltip,
    HStack,
    IconButton
} from '@chakra-ui/react'
import { TypeBadge } from '../TypeBadge/TypeBadge'
import fieldManifest from '../../fieldManifest'
import {
    LPTd,
    MessageDetailsTableWrapper,
    MessageDetailTableRow,
    TableScrollWrapper,
    ErrorMessage,
    TimelineTr
} from './styled'
import { capitalizeString } from '../../utils'
import { getDetailsArray, getValTdContent } from './helpers'
import { FiSearch } from 'react-icons/fi'
import { TbMessageCode } from 'react-icons/tb'
import { CiViewTable } from 'react-icons/ci'
import { CodeBlock } from '../../../fix-session-manager/components/common/CodeBlock'

interface Props {
    data?: any
    onSearchClOrdID: (searchRequest: any) => void
    isSearch?: boolean
}

export const MessageDetailsTable: FC<Props> = ({ data, onSearchClOrdID, isSearch }) => {
    const detailsArray = data ? getDetailsArray(data) : []
    const isError = data?.status?.includes('parse-error')

    const sortedDetailsArray =
        detailsArray
        .sort(({ key: aKey }, { key: bKey }) =>
            fieldManifest[capitalizeString(aKey)] && !fieldManifest[capitalizeString(bKey)] ? -1 : 1
        )
        .filter((value => !value.key.startsWith("_") && value.key !== 'seqNumber'))

    const [showFixMsg, setShowFixMsg] = useState(false)

    const renderRow = (
        item: {
            key: string
            value: any
            desc?: any
        },
        index: number
    ) => {
        const { key, value, desc } = item
        const capKey = capitalizeString(key)

        const valueDescription =
            key === 'msgType' ? desc?.replace(/([A-Z])/g, ' $1').toUpperCase() : desc?.toUpperCase()

        const isErrorRow = isError && (key === 'cause' || key === 'status')
        return (
            <MessageDetailTableRow key={index} isError={isErrorRow} isEven={index % 2 === 0}>
                <LPTd>{fieldManifest[capKey]}</LPTd>
                <LPTd>
                    <TypeBadge type={capKey} isDetails />
                </LPTd>
                <LPTd>{getValTdContent(key, value)}</LPTd>
                <LPTd style={{ whiteSpace: 'normal' }}>
                    {valueDescription}
                    {isSearch && key === 'clOrdID' && (
                        <Box display="flex" pr={2} w="100%" justifyContent="flex-end">
                            <Tooltip
                                label='View all the FIX messages with this ClOrdID'
                                shouldWrapChildren
                            >
                                <FiSearch
                                        onClick={() => {
                                            onSearchClOrdID({ query: `@ClOrdID == "${value}"` })
                                        }}
                                        style={{ cursor: 'pointer' }}
                                    />
                            </Tooltip>
                        </Box>
                    )}
                </LPTd>
            </MessageDetailTableRow>
        )
    }

    return (
        <MessageDetailsTableWrapper>
            <HStack spacing={1}>
                <Heading as="h5" size="md">
                    {showFixMsg ? 'FIX Message' : 'Details'}
                </Heading>
                <Tooltip label={`Show ${showFixMsg ? 'parsed message details' : 'FIX message'}`} openDelay={500}>
                    <IconButton
                        variant='ghost'
                        size='xs'
                        aria-label='Toggle FIX message view'
                        icon={showFixMsg ? <CiViewTable strokeWidth={0.5} size={18} /> : <TbMessageCode strokeWidth={1.5} size={18} />}
                        onClick={() => setShowFixMsg(!showFixMsg)}
                    />
                </Tooltip>
            </HStack>
            
            {showFixMsg ? (
                <Box
                    position='relative'
                    marginTop='8px'
                >
                    <CodeBlock whiteSpace='wrap' overflowWrap='anywhere'>
                        {data?.msg}
                    </CodeBlock>
                </Box>
            ) : (
                isError ? (
                    <ErrorMessage>
                        <Heading sx={{ textAlign: 'left', fontSize: 16, paddingBottom: 4 }} as="h5" size="md">
                            Error
                        </Heading>
                        <div>{data.cause.substring(0, data.cause.indexOf('msgSeqNum='))}</div>
                        {/* <div>{data.status}</div> */}
                    </ErrorMessage>
                ) :
                <TableScrollWrapper>
                    <Table
                        size="sm"
                        colorScheme="white"
                        fontFamily='var(--code-font-family)'
                        style={{
                            tableLayout: 'auto',
                            width: '100%',
                            height: 'fit-content',
                            minWidth: '470px'
                        }}
                    >
                        <Thead>
                            <TimelineTr>
                                <Th>Tag</Th>
                                <Th>Field Name</Th>
                                <Th>Value</Th>
                                <Th whiteSpace='nowrap'>Value Name</Th>
                            </TimelineTr>
                        </Thead>
                        <Tbody>{sortedDetailsArray.map(renderRow)}</Tbody>
                    </Table>
                </TableScrollWrapper>
            )}
        </MessageDetailsTableWrapper>
    )
}
